import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { CalendarDays } from 'lucide-react';
import { useLanguage } from '../../contexts/useLanguage';

type UpdateTravelInfoDialogProps = {
  open: boolean;
  /** Save dates/groups then close */
  onUpdate: () => void;
  /** Discard local edits and close */
  onDiscard: () => void;
  /** Stay on the assistant */
  onStay: () => void;
  /** When true, form is incomplete — only stay / discard */
  canSave?: boolean;
};

/**
 * Mobile-first confirm when leaving calendar assistant with unsaved travel info.
 * Portaled to body so it sits above sheet chrome; backdrop ignores the opening tap.
 */
export const UpdateTravelInfoDialog: React.FC<UpdateTravelInfoDialogProps> = ({
  open,
  onUpdate,
  onDiscard,
  onStay,
  canSave = true,
}) => {
  const { t } = useLanguage();
  const [backdropLive, setBackdropLive] = useState(false);

  useEffect(() => {
    if (!open) {
      setBackdropLive(false);
      return;
    }
    const timer = window.setTimeout(() => setBackdropLive(true), 120);
    return () => window.clearTimeout(timer);
  }, [open]);

  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[10080] flex items-end justify-center bg-black/45 p-4 sm:items-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="update-travel-title"
    >
      <button
        type="button"
        className="absolute inset-0 cursor-default"
        aria-label={t('common.cancel') || 'Cancel'}
        onClick={() => {
          if (!backdropLive) return;
          onStay();
        }}
      />
      <div className="relative z-10 w-full max-w-md rounded-2xl bg-white p-5 shadow-2xl">
        <div className="mb-3 flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#2f9e44]/12 text-[#2f9e44]">
            <CalendarDays className="h-5 w-5" aria-hidden />
          </div>
          <div className="min-w-0">
            <h2 id="update-travel-title" className="text-lg font-bold text-[#3d2817]">
              {t('shoppingCart.updateTravelInfo') || 'Update travel info'}
            </h2>
            <p className="mt-1 text-sm leading-relaxed text-gray-600">
              {t('shoppingCart.updateTravelInfoDesc') ||
                'Save your dates and group size so we can show the right availability.'}
            </p>
          </div>
        </div>

        <div className="mt-5 flex flex-col gap-2">
          {canSave ? (
            <button
              type="button"
              onClick={onUpdate}
              className="w-full rounded-xl bg-[#2f9e44] px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#27863a] active:scale-[0.99]"
            >
              {t('common.confirm') || 'Confirm'}
            </button>
          ) : null}
          <button
            type="button"
            onClick={onDiscard}
            className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm font-semibold text-[#3d2817] transition-colors hover:bg-gray-50 active:scale-[0.99]"
          >
            {t('shoppingCart.discardTravelInfo') || 'Discard changes'}
          </button>
          <button
            type="button"
            onClick={onStay}
            className="w-full rounded-xl px-4 py-2.5 text-sm font-medium text-gray-500 transition-colors hover:text-gray-700"
          >
            {t('common.cancel') || 'Cancel'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default UpdateTravelInfoDialog;
